/**
 * @fileoverview Utility functions for extracting and managing layout props from UiLayout component.
 * Provides helpers to get layout props with overrides and bind them to components.
 */

import UiLayout from '../Layout/Layout.vue'

const uiLayoutProps = UiLayout.options?.props || UiLayout.props || {}

/**
 * Extracts layout props from UiLayout component and applies overrides.
 * Renames 'type' prop to 'layout' and merges override options into existing props.
 * @param {Object} [overrides={}] - Object containing prop overrides to merge with layout props
 * @returns {Object} Layout props object with overrides applied
 */
const getLayoutProps = (overrides = {}) => {
    const { type, ...props } = uiLayoutProps

    const layoutProps = {
        ...props,
        layout: type,
    }

    Object.entries(overrides).forEach(([prop, options]) => {
        layoutProps[prop] = {
            ...layoutProps[prop],
            ...options,
        }
    })

    return layoutProps
}

/**
 * Generates layout prop bindings from a component instance.
 * Maps component properties to layout props, converting 'layout' back to 'type' where needed.
 * @param {Object} component - Vue component instance containing layout properties
 * @returns {Object} Object mapping layout prop names to their values from the component
 */
const getLayoutBindings = (component) =>
    Object.keys(uiLayoutProps).reduce((bindings, prop) => {
        bindings[prop] = component[prop === 'type' ? 'layout' : prop]

        return bindings
    }, {})

export { getLayoutProps, getLayoutBindings }
